<template>
  <!-- 弹层仅客户端渲染：避免 SSR Teleport hydration 在旧内核上 mismatch -->
  <ClientOnly>
    <Teleport to="body">
      <Transition name="app-drawer">
        <div
          v-if="open"
          class="app-drawer"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          :style="zIndex ? { zIndex } : undefined"
        >
          <header class="app-drawer__head">
            <button
              type="button"
              class="app-drawer__back"
              :aria-label="backLabel"
              @click="emit('back')"
            >
              ‹
            </button>
            <div
              class="app-drawer__title"
              :class="{ 'app-drawer__title--left': titleAlign === 'left' }"
            >
              {{ title }}
            </div>
            <div class="app-drawer__side">
              <slot name="action">
                <span class="app-drawer__spacer" />
              </slot>
            </div>
          </header>

          <div
            class="app-drawer__body"
            :class="{ 'app-drawer__body--padded': padded }"
            :style="padded && bodyPadding ? { padding: bodyPadding } : undefined"
          >
            <slot />
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
/**
 * 全屏右侧滑入二级页
 * 用于消息通知 / 游戏报表 / 额度转换 / 安全中心等
 */
const open = defineModel<boolean>({ default: false })

withDefaults(
  defineProps<{
    title: string
    /** 内容区左右与顶部内边距（报表/转换等）；通知/安全中心自管 section 边距 */
    padded?: boolean
    /** 覆盖默认 padding（仅在 padded 为 true 时生效） */
    bodyPadding?: string
    /** 覆盖默认 z-index（多层抽屉叠加时，上层需大于下层） */
    zIndex?: number
    /** 标题对齐，默认居中 */
    titleAlign?: 'center' | 'left'
  }>(),
  { padded: false, bodyPadding: undefined, zIndex: undefined, titleAlign: 'center' },
)

const emit = defineEmits<{
  back: []
}>()

const { t } = useI18n()
const backLabel = computed(() => t('common.back'))

const { lockWhile } = useBodyScrollLock()
lockWhile(open)
</script>

<style scoped lang="scss">
.app-drawer {
  position: fixed;
  /* Chrome < 87 无 inset，需显式四边 */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 55;
  max-width: 480px;
  margin: 0 auto;
  background: var(--bg);
  display: flex;
  flex-direction: column;
}

.app-drawer__head {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--appbar-bg);
  border-bottom: 1px solid var(--hairline);
  backdrop-filter: blur(8px);
}

.app-drawer__back {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: var(--r-sm);
  display: grid;
  place-items: center;
  font-size: 26px;
  line-height: 1;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  color: var(--text);
  padding: 0;
  position: relative;
  z-index: 1;

  &:active {
    opacity: 0.85;
  }
}

.app-drawer__title {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 900;
  color: var(--text);
  white-space: nowrap;
  text-align: center;
  pointer-events: none;

  &--left {
    justify-content: flex-start;
    padding-left: 58px;
  }
}

.app-drawer__side {
  margin-left: auto;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 34px;
  min-height: 34px;
}

.app-drawer__spacer {
  width: 34px;
  flex: 0 0 34px;
}

.app-drawer__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: calc(28px + env(safe-area-inset-bottom, 0px));
  -webkit-overflow-scrolling: touch;

  &--padded {
    padding: 12px 14px calc(28px + env(safe-area-inset-bottom, 0px));
  }
}

.app-drawer-enter-active,
.app-drawer-leave-active {
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.app-drawer-enter-from,
.app-drawer-leave-to {
  transform: translateX(100%);
}
</style>

<!-- #action 插槽按钮可用类名（非 scoped，供业务页复用） -->
<style lang="scss">
.app-drawer__act {
  padding: 0 2px;
  border: 0;
  background: none;
  font-size: 13px;
  font-weight: 700;
  color: var(--primary);
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  &:active:not(:disabled) {
    opacity: 0.85;
  }
}
</style>
